import { Injectable } from '@angular/core';

@Injectable({ providedIn: 'root' })
export class CookieService {

  private readonly maxChunkSize = 4000;
  private readonly samesite = 'SameSite=Lax';

  private buildCookieAttributes(day?: number): string {
    let expires = '';

    if (day) {
      expires = `; Expires=${new Date(Date.now() + day * 864e5).toUTCString()}`;
    }

    const path = '; Path=/';

    return `${expires}${path}; ${this.samesite}`;
  }

  private setSingle(name: string, value: string, day?: number) {
    document.cookie =
      `${name}=${encodeURIComponent(value)}${this.buildCookieAttributes(day)}`;
  }

  private getSingle(name: string): string | null {
    return document.cookie
      .split('; ')
      .find(x => x.startsWith(name + '='))?.split('=')[1] ?? null;
  }

  private deleteSingle(name: string) {
    document.cookie =
      `${name}=; Expires=Thu, 01 Jan 1970 00:00:00 GMT; Path=/; ${this.samesite}`;
  }

  set(name: string, value: string, day?: number) {
    this.delete(name);

    const encoder = new TextEncoder();
    const bytes = encoder.encode(value);

    let index = 0;
    for (let start = 0; start < bytes.length; start += this.maxChunkSize) {
      const chunkBytes = bytes.slice(start, start + this.maxChunkSize);
      const chunk = new TextDecoder().decode(chunkBytes);

      this.setSingle(`${name}_${index}`, chunk, day);
      index++;
    }

    this.setSingle(`${name}_count`, String(index), day);
  }

  get(name: string): string | null {
    const countStr = this.getSingle(`${name}_count`);
    if (!countStr) return null;

    const total = Number(countStr);
    if (isNaN(total) || total <= 0) return null;

    let result = '';
    for (let i = 0; i < total; i++) {
      const part = this.getSingle(`${name}_${i}`);
      if (!part) return null;
      result += decodeURIComponent(part);
    }

    return result;
  }

  delete(name: string) {
    const countStr = this.getSingle(`${name}_count`);
    const total = Number(countStr ?? 0);

    this.deleteSingle(`${name}_count`);

    for (let i = 0; i < total; i++) {
      this.deleteSingle(`${name}_${i}`);
    }
  }

  deleteAll(): void {
    document.cookie.split(';').forEach(cookie => {
      const name = cookie.split('=')[0].trim();
      document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`;
    });
  }
}
